// "What is this?" content for every card and metric in the UI.
//
// Written to work on two levels at once: someone who has never taken a biology
// class should finish a panel understanding what they are looking at, and
// someone who works in genomics should find nothing wrong and at least one
// thing they enjoy. That means real terminology (introduced, not assumed),
// real numbers, and honest limits — never a simplification that is false.
//
// Content only. Rendering lives in components/Explain.tsx.

export interface ExplainerSection {
  heading: string;
  paras: string[];
  /** Compact key→value table rendered under the prose. */
  facts?: [string, string][];
}

export interface Explainer {
  title: string;
  /** One sentence, no jargon. The thing you'd say to a friend. */
  hook: string;
  sections: ExplainerSection[];
  /** How to read the specific numbers on screen. */
  reading?: string[];
  /** What this genuinely cannot tell you. */
  limits?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────

const DNA_BASICS: ExplainerSection = {
  heading: 'Start here: what the data actually is',
  paras: [
    'Your genome is a sequence of about 3.1 billion chemical letters, written in a four-letter alphabet: A, C, G, and T. It is stored in 46 chromosomes — 23 inherited from each parent — so you carry two copies of almost every position.',
    'Any two unrelated people are about 99.9% identical across that sequence. Everything that differs between you and someone else lives in the remaining 0.1%, which still amounts to roughly 4–5 million differences. A single-letter difference is called a SNP (pronounced "snip") — a single-nucleotide polymorphism.',
    'A consumer test does not read your whole genome. It checks a fixed, pre-chosen panel of positions — typically 600,000 to 1 million of them — that are already known to vary between people. That is about 0.02% of your DNA, deliberately chosen to be the interesting 0.02%.',
  ],
};

export const EXPLAINERS: Record<string, Explainer> = {
  // ── Overview ──────────────────────────────────────────────────────────────
  snapshot: {
    title: 'Snapshot',
    hook: 'A first look at the file you just loaded — how much data it contains, and whether it looks healthy.',
    sections: [
      DNA_BASICS,
      {
        heading: 'What these four numbers measure',
        paras: [
          'None of these describe your traits or health. They describe the quality and shape of the file — the genomics equivalent of checking that a photo is in focus before you look at what it shows.',
          'Every genotyping lab reports these, and they are the first thing an analyst checks. A file that fails them cannot be trusted for anything downstream, no matter how interesting the results look.',
        ],
        facts: [
          ['Variants', 'How many positions the file contains'],
          ['Call rate', 'What fraction of those the machine could actually read'],
          ['Heterozygosity', 'How often your two chromosome copies disagree'],
          ['Ti/Tv', 'A chemical fingerprint of real mutation, used as a noise check'],
        ],
      },
    ],
    reading: [
      'Variants — for a 23andMe or AncestryDNA file, expect roughly 600,000–1,000,000. Far fewer means a partial or filtered file.',
      'Call rate — above 98% is a healthy array. Below 95% suggests a degraded or low-quality sample.',
      'Heterozygosity — on an array, roughly 30–35% is typical. Much higher can indicate a contaminated or mixed sample; much lower can reflect parental relatedness or a population bottleneck.',
    ],
    limits: [
      'A file can pass every check here and still be the wrong person\'s. These metrics detect technical failure, not mix-ups.',
      'None of these numbers say anything about traits, ancestry, or health.',
    ],
  },

  chromosomes: {
    title: 'Variants per chromosome',
    hook: 'Your DNA is packaged into 46 separate bundles. This shows how the tested positions are spread across them.',
    sections: [
      {
        heading: 'The 24 distinct chromosomes',
        paras: [
          'Humans have 23 pairs of chromosomes. Pairs 1–22 are called autosomes and are numbered by size, largest first — chromosome 1 is about 249 million letters, chromosome 21 only about 47 million. The 23rd pair determines sex chromosomally: XX or XY.',
          'There is a 25th entry here that is not a chromosome pair at all. MT is the mitochondrial genome — a tiny 16,569-letter loop inside your cells\' power plants. You inherit it exclusively from your mother, there are hundreds of copies per cell, and it mutates roughly ten times faster than nuclear DNA. That combination is why it is the workhorse of maternal-lineage and ancient-DNA studies.',
        ],
        facts: [
          ['Chromosome 1', '~249 million base pairs — the largest'],
          ['Chromosome 21', '~47 million — the smallest autosome'],
          ['X', '~156 million; Y is only ~57 million and gene-poor'],
          ['MT', '16,569 base pairs, maternally inherited, ~37 genes'],
        ],
      },
      {
        heading: 'Why the bars slope the way they do',
        paras: [
          'Bar height should track chromosome length fairly closely, because array designers space markers roughly evenly along the genome. The slope you see from left to right is mostly just chromosomes getting smaller.',
          'Deviations are informative. Chromosome 19 punches above its size because it is unusually gene-dense. The Y chromosome is always sparse — it is small, largely repetitive, and only present in one copy in XY individuals. MT is a sliver.',
        ],
      },
    ],
    reading: [
      'A smooth descending staircase is normal and boring, which is what you want.',
      'One chromosome dramatically out of line with its neighbours is worth a second look — in real data that can indicate a large duplication or deletion, though on an array it is more often a design quirk.',
      'No Y bar at all is the expected result for an XX genome, not an error.',
    ],
  },

  // ── Browse ────────────────────────────────────────────────────────────────
  browse: {
    title: 'Browsing variants',
    hook: 'The raw list — every single position your test read, and what it found there.',
    sections: [
      {
        heading: 'rsID — the universal name for a position',
        paras: [
          'An rsID (like rs7903146) is a Reference SNP cluster ID, assigned by dbSNP, the variant database run by the US National Center for Biotechnology Information. It is a stable, global name: rs7903146 refers to the same position on chromosome 10 for every lab, paper, and database in the world.',
          'This is more useful than it sounds. Genome coordinates shift between reference assemblies — the same variant sits at different numbers in GRCh37 and GRCh38 — but the rsID never changes. It is the join key that makes the whole field interoperable.',
          'A dot (.) instead of an rsID means the variant has no assigned identifier, usually because it is novel or too rare to have been catalogued.',
        ],
      },
      {
        heading: 'Genotype — why there are two letters',
        paras: [
          'You have two copies of each autosome, so at every position you have two readings. "AG" means one chromosome carries A and the other carries G. Which parent contributed which is not recorded — an array cannot tell, because it reads both copies at once and simply reports what it saw.',
          'When the two letters match (AA, GG) the position is homozygous. When they differ (AG) it is heterozygous. When the machine could not get a reading it reports "--", a no-call.',
        ],
        facts: [
          ['AA / GG / CC / TT', 'Homozygous — both copies agree'],
          ['AG / CT / …', 'Heterozygous — the copies differ'],
          ['--', 'No-call — the assay failed at this position'],
        ],
      },
      {
        heading: 'The strand problem (this one trips up professionals too)',
        paras: [
          'DNA is a double helix of two complementary strands. A always pairs with T, and C always pairs with G. So a position that reads "AG" on one strand reads "TC" on the other — the same biological fact, written two different ways.',
          'There is no universal convention for which strand a consumer file reports. 23andMe, AncestryDNA, and dbSNP have not always agreed, and formats have changed between chip versions. Comparing genotypes across sources without accounting for this produces silently wrong answers, and it is one of the most common errors in amateur genomics.',
          'i-seng handles this in the Conditions tab: it checks your call against both the expected alleles and their complements, and flips when it can tell. For A/T and C/G positions it genuinely cannot tell — those read identically on both strands — so it flags them rather than guessing.',
        ],
      },
    ],
    reading: [
      'Search accepts rsIDs, positions, and genotypes — try "rs1815739", or "AG" to see every heterozygous call.',
      'Position is the base-pair offset along that chromosome, counting from 1.',
      'Ref/Alt columns appear only for VCF files: the reference genome\'s letter versus what differs in yours.',
    ],
    limits: [
      'The order of the two letters carries no meaning. "AG" and "GA" are the same result.',
      'You cannot tell from an array which parent gave you which allele, or whether two variants on the same chromosome sit on the same copy. That information is called phase, and recovering it needs either sequencing of long molecules or genotypes from your relatives.',
    ],
  },

  // ── Conditions ────────────────────────────────────────────────────────────
  conditions: {
    title: 'How condition associations work',
    hook: 'Researchers compared thousands of people with a condition to thousands without, and found letters that showed up more often in one group. That is all an "association" is.',
    sections: [
      {
        heading: 'Where these findings come from',
        paras: [
          'Almost everything in this tab comes from genome-wide association studies (GWAS). The method is blunt and powerful: genotype tens of thousands of people, record who has the condition, and test every position in the genome for a frequency difference between the two groups.',
          'Because that means running about a million statistical tests at once, the bar for significance is set brutally high — p < 5×10⁻⁸, rather than the usual 0.05. That threshold is a Bonferroni correction for roughly a million independent tests, and it is why GWAS findings replicate far better than the candidate-gene studies that preceded them.',
          'A finding that clears that bar is real in the sense that the frequency difference is not chance. It does not follow that the variant causes anything — it is usually just physically close to whatever does.',
        ],
      },
      {
        heading: 'Odds ratios, and why 1.4× is smaller than it sounds',
        paras: [
          'Effect sizes are given as odds ratios. An OR of 1.4 means people carrying the variant are about 1.4 times as likely to have the condition — as a group average, across a whole population.',
          'The trap is confusing relative risk with absolute risk. If a condition affects 1% of people, a 1.4× variant moves that to roughly 1.4%. You have gained 0.4 percentage points of absolute risk. Reported as "40% higher risk", it sounds alarming; reported as "1.4 in 100 instead of 1 in 100", it sounds like what it is.',
          'Most common variants sit between 1.1× and 1.5×. Anything above 2× for a common variant is unusual, which is why CFH in macular degeneration (~2.5×) and HLA-B*27 in ankylosing spondylitis are famous.',
        ],
        facts: [
          ['OR ≈ 1.0', 'No detectable effect'],
          ['OR 1.1–1.5', 'Typical common-variant effect — small'],
          ['OR 2–5', 'Large for a common variant; rare'],
          ['OR > 5', 'Usually a rare, clinically significant mutation'],
        ],
      },
      {
        heading: 'Effect alleles and dosage',
        paras: [
          'Each association names one allele — one of the two letters — as the effect allele. You can carry zero, one, or two copies of it, and effects usually scale roughly with that count. This is called an additive model, and it fits most common variants well.',
          'The direction can go either way. A protective allele lowers the odds instead of raising them: IL23R rs11209026 is associated with roughly 0.4× the odds of Crohn\'s disease, one of the strongest protective effects known in complex disease.',
        ],
      },
      {
        heading: 'Why your ancestry changes what this means',
        paras: [
          'The overwhelming majority of GWAS participants have been of European ancestry. An odds ratio measured in that population may be smaller, larger, or entirely absent in another — partly because the true causal variant sits at different distances from the tested marker in different populations, and partly because allele frequencies themselves differ enormously.',
          'PTPN22 rs2476601, a major autoimmune risk variant in Europeans, is essentially absent from East Asian populations. ALDH2 rs671, which causes the alcohol flush reaction, is common in East Asia and vanishingly rare elsewhere. Neither variant is more or less "real" — they are just differently distributed.',
        ],
      },
    ],
    reading: [
      'Your call is shown against the effect allele, with how many copies you carry.',
      'A "strand ?" flag means the position is A/T or C/G, where orientation cannot be determined — treat that row with extra caution.',
      'A "flipped" flag means your file reported the opposite strand and i-seng corrected it.',
      '"0 / N markers in your file" means your test simply did not include those positions.',
    ],
    limits: [
      'Not a diagnosis, and not a risk calculator. There is deliberately no combined score — summing odds ratios across markers implies a precision that genotype data does not support.',
      'A missing marker tells you nothing. Consumer arrays cover ~0.02% of the genome and almost none of the rare, high-impact mutations that clinical testing looks for.',
      'These are population statistics. They describe groups of thousands of people, and cannot tell you what will happen to any individual — including you.',
    ],
  },

  // ── Analysis ──────────────────────────────────────────────────────────────
  genotypeDist: {
    title: 'Genotype distribution',
    hook: 'A tally of how often each two-letter result appears in your file.',
    sections: [
      {
        heading: 'What shapes this chart',
        paras: [
          'The counts are driven by two things: which positions the array chose to test, and how common each allele is in human populations. Array designers deliberately pick positions where variation is common, so the distribution here is not representative of your genome as a whole.',
          'Homozygous calls dominate, which is expected. For a variant where the minor allele has frequency q, Hardy–Weinberg equilibrium predicts genotype proportions of (1−q)², 2q(1−q), and q² — so unless q is close to 0.5, the two homozygous classes together outnumber the heterozygotes.',
        ],
      },
      {
        heading: 'Hardy–Weinberg equilibrium',
        paras: [
          'That formula is the closest thing population genetics has to a null hypothesis. It states that in a large, randomly mating population with no selection or migration, allele frequencies predict genotype frequencies exactly, and stay constant across generations.',
          'Real populations violate its assumptions constantly, which is precisely why it is useful: a position whose observed genotypes deviate sharply from the prediction is flagged as suspect. In practice, a strong HWE deviation in a genotyping dataset usually means a technical failure at that position rather than anything biological.',
        ],
      },
    ],
    reading: [
      'The top bars will normally be homozygous pairs (AA, CC, GG, TT).',
      'The "--" bar is your no-calls. A tall one means poor sample quality.',
    ],
  },

  zygosity: {
    title: 'Zygosity',
    hook: 'For each tested position: did your two chromosome copies agree, disagree, or fail to read?',
    sections: [
      {
        heading: 'Two copies, three outcomes',
        paras: [
          'You inherited one copy of each autosome from each parent. Homozygous means both copies carry the same letter at that position. Heterozygous means they differ. A no-call means the assay failed and no result exists.',
          'Heterozygosity is one of the most informative summary numbers in population genetics, because it is a direct readout of how much genetic diversity an individual carries.',
        ],
      },
      {
        heading: 'What an unusual rate would mean',
        paras: [
          'Depressed heterozygosity across the genome indicates that your parents shared recent ancestry, or that your population passed through a bottleneck. Long unbroken stretches of homozygous calls — runs of homozygosity — are the specific signature, and their length indicates how many generations back the shared ancestor sits.',
          'Elevated heterozygosity is almost always technical rather than biological. Two people\'s DNA mixed in one sample produces excess heterozygous calls, because positions where the two donors differ get read as both letters. Contamination checks in sequencing pipelines work on exactly this principle.',
        ],
      },
    ],
    reading: [
      'On a consumer array, roughly 30–35% of called positions being heterozygous is typical.',
      'That figure is far higher than the genome-wide truth (~0.1% of all bases differ between your two copies) because arrays deliberately test positions already known to vary.',
    ],
  },

  quality: {
    title: 'Quality metrics — transitions, transversions, and Ti/Tv',
    hook: 'A chemistry-based lie detector for your data. Real mutations leave a specific fingerprint; noise does not.',
    sections: [
      {
        heading: 'Two kinds of letter swap',
        paras: [
          'The four DNA bases fall into two chemical families. A and G are purines — larger, double-ring molecules. C and T are pyrimidines — smaller, single-ring.',
          'A mutation that swaps a letter for one in the same family (A↔G or C↔T) is a transition. A swap across families (A↔C, A↔T, G↔C, G↔T) is a transversion.',
          'Count the possibilities and there are twice as many potential transversions as transitions. So if mutation were purely random, you would expect a Ti/Tv ratio around 0.5.',
        ],
        facts: [
          ['Transition', 'A↔G or C↔T — within a chemical family'],
          ['Transversion', 'Any purine↔pyrimidine swap'],
          ['Random expectation', 'Ti/Tv ≈ 0.5'],
          ['Real human genome', 'Ti/Tv ≈ 2.0–2.1'],
          ['Protein-coding exons', 'Ti/Tv ≈ 3.0'],
        ],
      },
      {
        heading: 'Why reality is four times higher than chance',
        paras: [
          'Real human genomes run at about 2.0–2.1, and there are two reasons.',
          'The first is chemical. Cytosine that carries a methyl group — a common epigenetic mark, especially where a C sits next to a G — spontaneously deaminates into thymine. That single reaction is the most frequent source of point mutation in the human genome, and it produces a C→T transition every time.',
          'The second is selection. Because of how the genetic code is arranged, transitions are more likely than transversions to leave the encoded amino acid unchanged. Silent changes are invisible to natural selection, so they survive. That effect is strongest in protein-coding regions, which is why exome data runs near 3.0 rather than 2.1.',
        ],
      },
      {
        heading: 'How it is actually used',
        paras: [
          'Ti/Tv is the standard first-pass quality check on variant calls. False-positive calls arise from random sequencing errors, which have no chemical preference and therefore sit near 0.5. Contaminate a real call set with noise and the ratio drops toward that floor, proportionally to how much noise you added.',
          'A pipeline reporting Ti/Tv of 1.4 on whole-genome data is telling you, without any other evidence, that a substantial fraction of its calls are wrong.',
        ],
      },
    ],
    reading: [
      'Near 2.0 on sequencing data: healthy.',
      'Drifting toward 0.5: increasing proportion of false positives.',
      'Around 3.0 on exome data: also healthy — coding regions genuinely run higher.',
    ],
    limits: [
      'On a genotyping array this number is close to meaningless, and the value shown here may look badly wrong. That is expected: array positions were hand-picked by the chip designer, not discovered by sequencing, so the ratio reflects their selection criteria rather than your mutational history.',
      'The synthetic sample genome will show a nonsensical value, because its genotypes are generated at random — random letters produce the ~0.5 chance ratio, exactly as the theory predicts.',
    ],
  },

  // ── Privacy ───────────────────────────────────────────────────────────────
  privacy: {
    title: 'How the privacy guarantee works',
    hook: 'The page is built so that sending your data anywhere is not merely discouraged — it is blocked by the browser itself.',
    sections: [
      {
        heading: 'Content-Security-Policy',
        paras: [
          'Every web page can ship a Content-Security-Policy: a set of rules the browser enforces on that page, regardless of what the page\'s code tries to do. The built version of i-seng ships one containing the directive connect-src \'none\'.',
          'That directive blocks every outbound request the page could make — fetch, XMLHttpRequest, WebSocket, EventSource, beacons. Not "the app does not call them": the browser refuses. A bug, a malicious dependency, or a compromised build could not exfiltrate your genome, because the request never leaves the browser process.',
          'The dev server relaxes exactly two directives so hot-reload can talk to localhost. Neither exists in the built output.',
        ],
        facts: [
          ["connect-src 'none'", 'No network requests of any kind'],
          ["default-src 'self'", 'Resources only from the page itself'],
          ["object-src 'none'", 'No plugins or embedded objects'],
          ["form-action 'none'", 'No form can submit anywhere'],
        ],
      },
      {
        heading: 'Why genome data deserves this treatment',
        paras: [
          'Genetic data has properties that make ordinary "we take privacy seriously" handling inadequate. It is permanent — you cannot rotate it after a breach. It is identifying — a few dozen SNPs are enough to single you out. And it is not only yours: you share roughly 50% with each parent, sibling, and child, so disclosing your genome partially discloses theirs, without their consent.',
          'That last property is the one most people miss, and it is why the safest architecture is one where the data simply never moves.',
        ],
      },
    ],
    limits: [
      'This protects the analysis, not the sequencing. Whoever generated your file handled your physical sample and holds their own copy — that exposure is upstream of anything this app can control.',
      'The clipboard and file downloads are deliberately not blocked, since those are how you get your own notes out. What you paste elsewhere is on you.',
    ],
  },

  // ── Individual metrics ────────────────────────────────────────────────────
  metricVariants: {
    title: 'Variants',
    hook: 'How many positions your test actually reported a result for.',
    sections: [
      {
        heading: 'What counts as a variant here',
        paras: [
          'Every row in your file is one position that was examined. On an array these are pre-chosen sites known to vary between people; in a VCF they are places where you differ from the reference genome. Both are counted the same way in this number.',
          'The figure tells you which kind of test produced the file more reliably than the filename does. A number near 600,000-1,000,000 is a genotyping array. Several million means sequencing.',
        ],
        facts: [
          ['~600k-1M', '23andMe / AncestryDNA array'],
          ['~50k-100k', 'Exome — coding regions only'],
          ['~4-5M', 'Whole-genome sequencing'],
          ['~3.1 billion', 'Bases in the genome, for scale'],
        ],
      },
    ],
    reading: [
      'Far below 500,000 for an array file usually means a partial download or a filtered export.',
      'The chromosome count beneath should be 24-25 for a complete file (22 autosomes, X, usually Y, often MT).',
    ],
    limits: [
      'More variants is not a better genome, only a more thoroughly examined one.',
      'An array reads roughly 0.02% of your DNA. This number is large and the coverage is still tiny.',
    ],
  },

  metricCallRate: {
    title: 'Call rate',
    hook: 'The proportion of tested positions where the machine actually got a readable answer.',
    sections: [
      {
        heading: 'Why a test fails at a position',
        paras: [
          'A genotyping array works by having your DNA stick to short probe sequences on a chip, then reading the fluorescent signal. Sometimes that fails: the DNA was degraded, the amount was too low, the probe sits over another variant that stops it binding, or the signal was too ambiguous to call confidently.',
          'Rather than guess, the pipeline emits a no-call — written "--" in 23andMe files, "./." in a VCF. That is a refusal to answer, which is the right behaviour and much safer than a coin flip.',
        ],
        facts: [
          ['> 98%', 'Healthy array'],
          ['95-98%', 'Usable, slightly degraded sample'],
          ['< 95%', 'Poor sample — treat results cautiously'],
          ['Per-sample QC', 'Labs typically reject below ~98.5%'],
        ],
      },
      {
        heading: 'The asymmetry worth knowing',
        paras: [
          'No-calls are not spread evenly. They cluster in regions that are hard to assay — repetitive sequence, segmental duplications, and positions where a nearby variant disrupts probe binding. So a missing result is more likely at exactly the awkward sites, not at random ones.',
          'That is why a no-call at a specific marker you care about means "not measured", never "normal".',
        ],
      },
    ],
    reading: [
      'The count beneath is the raw number of positions with no result.',
      'A low call rate depresses everything downstream — fewer called sites means noisier heterozygosity and Ti/Tv.',
    ],
    limits: [
      'A high call rate says the chemistry worked. It says nothing about whether the sample was yours.',
    ],
  },

  metricHet: {
    title: 'Heterozygosity',
    hook: 'How often your two copies of a chromosome disagree at a tested position.',
    sections: [
      {
        heading: 'What the number is measuring',
        paras: [
          'You carry two copies of each autosome, one from each parent. At any position they either match (homozygous) or differ (heterozygous). This is the share of successfully called positions where they differ.',
          'On a consumer array roughly 30-35% is typical. That figure is much higher than the genome-wide truth — only about 0.1% of all your bases differ between your two copies — because arrays deliberately test positions already known to be variable. You are looking at a heavily enriched sample, not a random one.',
        ],
        facts: [
          ['30-35%', 'Typical on a consumer array'],
          ['~0.1%', 'Across all bases, genome-wide'],
          ['Lower', 'Parental relatedness or a population bottleneck'],
          ['Higher', 'Usually sample contamination, not biology'],
        ],
      },
      {
        heading: 'Runs of homozygosity',
        paras: [
          'Depressed heterozygosity matters most when it is clustered rather than spread out. Long unbroken stretches of homozygous calls — runs of homozygosity — indicate that both copies of that stretch descend from a single recent ancestor.',
          'Their length is informative in itself: recombination breaks these segments up a little each generation, so long runs mean a recent shared ancestor and short scattered ones mean ancient, population-level relatedness. i-seng does not currently compute them.',
        ],
      },
      {
        heading: 'Why unusually high is a technical alarm',
        paras: [
          'Two people\'s DNA mixed into one sample produces excess heterozygous calls, because every position where the two donors differ reads as though one person carried both alleles. Contamination detection in sequencing pipelines works on precisely this signal.',
          'So elevated heterozygosity is nearly always a laboratory event rather than anything about you.',
        ],
      },
    ],
    limits: [
      'Ancestry shifts the baseline. African-ancestry genomes carry more variation, so this number is not comparable across populations without care.',
      'X-chromosome positions distort it for XY genomes, which have only one copy.',
    ],
  },

  metricTiTv: {
    title: 'Ti/Tv ratio',
    hook: 'A chemistry-based check on whether your data looks like real mutation or like noise.',
    sections: [
      {
        heading: 'Two kinds of letter swap',
        paras: [
          'A and G are purines — larger, two-ring molecules. C and T are pyrimidines — smaller, one ring. A change within a family (A↔G, C↔T) is a transition. A change across families is a transversion.',
          'There are twice as many possible transversions as transitions, so pure chance would give a ratio near 0.5. Real human data sits around 2.0-2.1, roughly four times higher than chance.',
        ],
        facts: [
          ['~0.5', 'What random noise produces'],
          ['~2.0-2.1', 'Real whole-genome data'],
          ['~3.0', 'Protein-coding exomes'],
          ['Falling toward 0.5', 'Rising proportion of false calls'],
        ],
      },
      {
        heading: 'Why reality beats chance',
        paras: [
          'Two reasons. Chemically, cytosine carrying a methyl group spontaneously deaminates into thymine — a C→T transition, and the single most common mutation in the human genome. And through selection: the genetic code is arranged so transitions more often leave the encoded amino acid unchanged, so they are removed less often. That second effect is why exomes run near 3.0 rather than 2.1.',
        ],
      },
    ],
    reading: [
      'On sequencing data, near 2.0 is healthy and drifting toward 0.5 signals false positives.',
      'On a genotyping array — including the sample genome — this number is close to meaningless.',
    ],
    limits: [
      'Array positions were hand-picked by the chip designer rather than discovered, so the ratio reflects their selection criteria, not your mutational history. Expect a value that looks wrong; that is the honest answer, not a bug.',
    ],
  },

  nucleus: {
    title: 'The nucleus view',
    hook: 'Every position your test read, all at once, as a point in space — so you can see how many there really are.',
    sections: [
      {
        heading: 'Why it is shaped like this',
        paras: [
          'The arrangement is not decorative. Two real facts about cell nuclei drive it.',
          'First, chromosome territories. For most of a cell\'s life its chromosomes are not a tangled soup — each occupies its own broadly separate region of the nucleus. Cremer and Cremer established this in 2001, and it overturned the older picture of freely intermingling chromatin. So each chromosome gets its own volume here, radiating from the centre.',
          'Second, chromatin is a coiled polymer. Your DNA winds around histone proteins into nucleosomes, those coil into a fibre roughly 30 nanometres across, and that loops again. A helical coil per chromosome is a fair cartoon of that packing, and it has a useful property: genomic neighbours stay spatial neighbours, so a dense run of markers reads as a dense patch rather than being scattered across the view.',
        ],
        facts: [
          ['~2 metres', 'DNA in a single human cell, end to end'],
          ['~6 µm', 'Diameter of the nucleus it fits inside'],
          ['~1 in 300,000', 'That is the linear compaction ratio'],
          ['~147 bp', 'DNA wound around each nucleosome'],
        ],
      },
      {
        heading: 'Reading brightness as density',
        paras: [
          'Points are drawn with additive blending: where several overlap, their light adds up. So brightness is a direct readout of how many tested positions sit in that stretch — the glowing regions are where your array sampled most heavily, and the dark gaps are where it could not.',
          'Those gaps are informative. The largest ones sit at centromeres, which are built from long repetitive sequence that probes cannot bind uniquely. You are looking at the limits of the assay, drawn in negative space.',
        ],
      },
      {
        heading: 'The scale problem this is meant to convey',
        paras: [
          'A consumer array reads somewhere around 600,000 positions, which sounds enormous until you put it next to 3.1 billion. Every point in this view together accounts for roughly two-hundredths of one percent of your genome.',
          'The pale highlighted points are the markers this app\'s condition catalog knows about — under two hundred of them. Turn on "condition markers only" and the view goes nearly black. That emptiness is the honest picture of how much of a genome any interpretation tool is actually reading.',
        ],
      },
    ],
    reading: [
      'Drag to orbit, scroll to fly in. Points grow as you approach, so you can zoom until individual variants separate.',
      'Each colour is one chromosome; the coils radiate outward with length scaled to chromosome size.',
      'Distance along a coil from the centre is position along that chromosome, p-arm first.',
    ],
    limits: [
      'This is NOT a real three-dimensional genome structure. Actual conformations are measured with Hi-C, differ between cell types and between individuals, and change through the cell cycle. Nothing in a genotype file constrains them.',
      'Positions here are derived from genomic coordinates alone — the coil is a visualisation device, not a measurement.',
      'Real territories are irregular blobs that touch and interdigitate at their edges, not tidy radiating coils.',
    ],
  },

  ideogram: {
    title: 'The genome map',
    hook: 'A picture of all 24 chromosomes, drawn to scale, with your tested positions marked along them.',
    sections: [
      {
        heading: 'Why chromosomes have stripes',
        paras: [
          'The banding pattern is real, and it predates DNA sequencing by decades. Stain a chromosome with Giemsa dye and it takes up the dye unevenly: some regions go dark, others stay pale. Cytogeneticists have used that pattern since the 1970s to identify chromosomes down the microscope and to spot large rearrangements.',
          'The pattern is not arbitrary. Dark bands (gpos) are AT-rich, gene-poor, and replicate late in the cell cycle. Pale bands (gneg) are GC-rich, gene-dense, and replicate early. So the stripes are a crude map of where the genes are — chromosome 19, which looks pale almost end to end, is the most gene-dense chromosome in the genome.',
          'The pinched red band on each chromosome is the centromere, where the two copies are held together during division. It is packed with repetitive sequence that no short-read technology can read through, which is why it stayed a gap in the reference genome until the T2T-CHM13 assembly finished it in 2022.',
        ],
        facts: [
          ['gneg', 'Pale — GC-rich, gene-dense, early-replicating'],
          ['gpos25 → gpos100', 'Increasingly dark — AT-rich, gene-poor'],
          ['acen', 'Centromere — repetitive, hard to sequence'],
          ['gvar', 'Variable-length region, differs between people'],
          ['stalk', 'Ribosomal RNA gene arrays, on five chromosomes'],
        ],
      },
      {
        heading: 'What the two tracks show',
        paras: [
          'The shaded column to the left of each chromosome is the density of your own tested positions. It is not a measure of anything biological about you — it reflects where the array designer chose to put probes. Gaps usually mean repetitive sequence that probes cannot bind uniquely, and the largest gaps line up with the centromeres.',
          'The dots to the right are the positions of markers that this app\'s condition catalog knows about and that your file actually contains. That is a useful sanity check on coverage: it shows at a glance how thin the catalog\'s sampling of your genome really is.',
        ],
      },
      {
        heading: 'Coordinates, and why the assembly selector exists',
        paras: [
          'A position like "chr7:117,559,590" is only meaningful relative to a specific reference assembly. GRCh37 (hg19) and GRCh38 (hg38) number the same genome differently, because GRCh38 corrected thousands of errors and closed gaps, shifting everything downstream of each change.',
          'The shifts are big enough to matter here. Chromosome 1 is 249,250,621 bases in GRCh37 and 248,956,422 in GRCh38 — a difference of nearly 300 kb. Plot GRCh37 coordinates against GRCh38 bands and variants land in visibly wrong places.',
          '23andMe and AncestryDNA both export GRCh37, and have for years. Whole-genome sequencing has largely moved to GRCh38. This app picks a default from your file format and shows you which one it assumed, because guessing silently is how coordinate bugs survive.',
        ],
        facts: [
          ['GRCh37 / hg19', '2009 — 23andMe, AncestryDNA raw exports'],
          ['GRCh38 / hg38', '2013 — most modern WGS and VCFs'],
          ['T2T-CHM13', '2022 — first truly complete, gapless human genome'],
        ],
      },
    ],
    reading: [
      'Chromosome heights are to scale, so chromosome 1 really is about five times chromosome 21.',
      'Hover any band for its cytogenetic name — the notation is chromosome, arm (p = short, q = long), then region and band, as in 17q21.',
      'The p arm is drawn at the top; "p" is for petit.',
    ],
    limits: [
      'MT is not shown. The mitochondrial genome is a 16.6 kb circle with no centromere and no cytogenetic bands — there is nothing to stain.',
      'Band boundaries are approximate by nature. They come from what is visible down a microscope, not from base-level annotation.',
      'This shows where your tested positions ARE, not what is at them. A dense region is a well-covered region, nothing more.',
    ],
  },

  pocket: {
    title: 'Why the pocket edition cannot read files',
    hook: 'This version of i-seng has no way to load a genome — not disabled, but absent from the code. That absence is the feature.',
    sections: [
      {
        heading: 'The problem with sharing genome tools',
        paras: [
          'The full i-seng never uploads anything: files are parsed in your browser and a strict Content-Security-Policy blocks every outbound request. A visitor risks nothing by looking at it.',
          'But a shared link changes the situation in a way that has nothing to do with this site being honest. If people get used to clicking a genome link someone sent them and loading their raw file into it, they have learned a reflex that a dishonest site can exploit. The attack is not this page — it is the habit.',
          'So the shareable edition removes the temptation at the source. There is no dropzone, no file picker, and no file parser in the JavaScript that was shipped to you. You can verify that: this page is small, unminified source maps are included, and searching the code for a file input finds nothing.',
        ],
      },
      {
        heading: 'What you are exploring instead',
        paras: [
          'The sample genomes are synthetic. Genotypes are drawn from real 1000 Genomes population frequencies under Hardy-Weinberg proportions, so the ancestry differences you see are true of populations — the alcohol-flush variant really is East Asian, sickle-cell trait really does concentrate in African-ancestry groups. But the individual is invented; no person\'s data is here.',
          'Everything else is the real application: the same condition catalog, the same explainers, the same chromosome map and 3D nucleus.',
        ],
      },
      {
        heading: 'If you have your own file',
        paras: [
          'The rule this edition is built to teach: never load your genome into a link someone sent you — anyone\'s link, including this one. A genome is permanent, identifying, and partly your relatives\' as well as yours.',
          'Run the full edition on your own machine instead, where the code is yours to inspect and the strict no-network policy stands between your data and the internet.',
        ],
      },
    ],
    limits: [
      'This edition proves the concept, not your genotype. Nothing here says anything about you.',
      'A synthetic genome has no linkage structure — real chromosomes travel in haplotypes; these alleles were drawn independently.',
    ],
  },

  reidentification: {
    title: 'Why your genome identifies you — and why anonymising it does not work',
    hook: 'A genome cannot be de-identified by removing your name. The sequence itself is the identifier, and roughly 75 common variants are enough to pick you out of everyone alive.',
    sections: [
      {
        heading: 'The number that settles it',
        paras: [
          'Lin, Owen and Altman (Science, 2004) worked out how many independent common SNPs it takes to make a genotype unique across the entire human population. The answer is about 75. A consumer array reads six hundred thousand.',
          'This is not a privacy policy question or a matter of how carefully a database is administered. It is arithmetic. Strip every name, address and account number from a genotype file and you still hold something that points at exactly one person — the same way a fingerprint does, except a fingerprint does not also implicate your family.',
          'The practical consequence: sharing "just a few SNPs" is not a safe middle ground. If you send someone a hundred rsIDs and your calls at them, you have sent them something identifying.',
        ],
        facts: [
          ['~75 SNPs', 'Enough to uniquely identify one human being'],
          ['~600,000', 'What a consumer array reads'],
          ['~50%', 'Shared with each parent, sibling, and child'],
          ['~12.5%', 'Shared with a first cousin'],
        ],
      },
      {
        heading: 'The channel you cannot close: your relatives',
        paras: [
          'Erlich and colleagues (Science, 2018) showed that a genealogy database covering just 2% of a population is enough to find a third-cousin-or-closer match for around 90% of people of European descent. From that match, ordinary genealogical records narrow the candidate down to one person — they demonstrated it on an anonymous genome in under a day.',
          'Consumer databases passed the 2% threshold years ago. This is the mechanism that identified the Golden State Killer in 2018: not his DNA in a database, but a distant relative\'s.',
          'The uncomfortable implication is that your genetic privacy is capped by the least private decision any of your relatives has already made — and you cannot retract it for them. A sibling or parent uploading exposes far more of you than a distant cousin does.',
        ],
      },
      {
        heading: 'Even summary statistics leak',
        paras: [
          'Homer and colleagues (2008) demonstrated that you can determine whether a specific individual was part of a study cohort using nothing but the aggregate allele frequencies published for that cohort — no individual data at all.',
          'The result was significant enough that the NIH withdrew public access to GWAS summary statistics that had previously been freely downloadable. It is worth knowing because it kills the intuition that averaging over many people is inherently safe.',
        ],
      },
      {
        heading: 'What actually reduces the risk',
        paras: [
          'Ranked by how much they help, not by how technical they sound. The first two matter more than everything below them combined.',
        ],
        facts: [
          ['Never upload it', 'GEDmatch, FamilyTreeDNA, MyHeritage, Promethease'],
          ['Talk to close family', 'GEDmatch and FTDNA permit law-enforcement matching'],
          ['Analyse locally', 'What this app is for'],
          ['Encrypt at rest', 'Never inside a synced cloud folder'],
          ['Pseudonymous account', 'Caps breach exposure at the provider'],
          ['Exclude chrY if sharing', 'Y-STRs can infer surnames (Gymrek 2013)'],
        ],
      },
    ],
    limits: [
      'None of this makes a genome anonymous. Risk can be reduced substantially; it cannot be eliminated, because the residual depends on choices your relatives have already made.',
      'Anyone who tells you genomic data can be meaningfully de-identified is mistaken. The scientific literature has been settled on this since 2004.',
      'In the US, GINA prohibits genetic discrimination in health insurance and employment — but explicitly not in life, disability, or long-term care insurance. Other jurisdictions differ; check yours.',
      'This app can protect the analysis. It cannot protect the sample you mailed to a sequencing lab, or anything you paste elsewhere.',
    ],
  },

  vault: {
    title: 'The note vault',
    hook: 'A staging area between noticing something and filing it in your own notes — with a deliberate split in what survives a reload.',
    sections: [
      {
        heading: 'Why two different persistence rules',
        paras: [
          'Queued notes embed your actual genotype calls. The core promise of this app is that your genome does not outlive the browser tab, so the queue is held in memory only — reload and it is gone. Writing it to storage would quietly break the guarantee the privacy banner makes.',
          'The ideas ledger is the opposite case. It is your own prose, it contains no genome data unless you type some, and a ledger that forgets is useless. So it persists in this browser\'s local storage. The asymmetry is deliberate, and it is the honest place to draw the line.',
        ],
        facts: [
          ['Note queue', 'Memory only — cleared on reload'],
          ['Ideas ledger', 'Persisted in this browser'],
          ['Downloads', 'Generated locally, never uploaded'],
          ['./brain/', 'Git-ignored, never synced'],
        ],
      },
      {
        heading: 'Why local files and not sync',
        paras: [
          'For genome notes the real exposure is not Obsidian, it is sync. iCloud, Google Drive, Dropbox, Synology Drive and Obsidian Sync all place copies on infrastructure outside your control, and most are not end-to-end encrypted.',
          'The model here is a local vault reached over Tailscale when you need it from another device — a private path to this machine rather than a third-party copy. If notes ever might land in a vault that does sync, the CLI writer takes a --redact flag that omits raw genotype calls and keeps only rsID, position, zygosity and your own commentary.',
        ],
      },
      {
        heading: 'How downloads work without a network',
        paras: [
          'The page cannot make network requests at all, so exports are assembled in memory as a Blob and handed to the browser as a download. Nothing is uploaded and nothing round-trips through a server — the bytes go straight from the tab to your disk.',
        ],
      },
    ],
    limits: [
      'Notes are only as private as where you paste them. The app can guarantee its own behaviour, not what happens to the text afterwards.',
      'The queue does not survive a reload by design. Export before you close the tab.',
    ],
  },

  // ── Loader ────────────────────────────────────────────────────────────────
  formats: {
    title: 'Which files you can load — and what each one really is',
    hook: 'Different tests produce very different amounts of data. The file format tells you which one you got.',
    sections: [
      {
        heading: 'Arrays versus sequencing',
        paras: [
          'A genotyping array does not read your DNA. It checks a fixed panel of pre-chosen positions using probes that bind to specific sequences — like looking up 700,000 specific entries in a 3.1-billion-entry book. It is cheap, fast, and blind to anything not on the list.',
          'Sequencing actually reads the letters. Whole-exome sequencing reads the ~1–2% of the genome that codes for proteins; whole-genome sequencing reads all of it. Both can discover variants nobody has ever seen before, which an array structurally cannot.',
        ],
        facts: [
          ['23andMe / AncestryDNA', '~600k–1M positions · 15–25 MB · array'],
          ['Exome VCF', '~1–2% of the genome · 50–200 MB'],
          ['Genome VCF', '~4–5M variants · 1–5 GB'],
          ['BAM / CRAM', 'The raw aligned reads · 30–120 GB'],
        ],
      },
      {
        heading: 'What a VCF contains',
        paras: [
          'Variant Call Format is the standard interchange format for sequencing results. Crucially it is a list of differences from a reference genome, not your full sequence — positions where you match the reference are simply absent, which is what keeps the file merely large rather than enormous.',
          'That design has a consequence worth understanding: absence of a position in a VCF means "same as reference", not "not tested". Those are very different statements, and conflating them is a classic source of error.',
        ],
      },
    ],
    limits: [
      'i-seng parses files in memory, so very large whole-genome VCFs will hit a cap. Array files and exome VCFs are comfortable.',
      'BAM and CRAM are not supported — they need indexed random access rather than a linear read.',
    ],
  },
};
